/*
 * ss-reviews.js — PDP reviews section.
 *
 * <ss-reviews> renders from the JSON in its <script type="application/json">
 * (one product entry), or, without one, from the file at data-src, fetched as
 * the section nears the viewport:
 *   { products: { [id]: entry }, pools: { [name]: pool } }
 *   entry = { summary?, featured?, fit?, reviews } for a product on its
 *           own, or { pool, featured? } for one that shares reviews with the
 *           same product in other styles (a shoe line, the jerseys, a
 *           design's colourways): its own reviews are the pool's carrying
 *           its pid
 *   pool  = { label, ownLabel, noun, bundle?, fit?, products?: { [pid]: {
 *             tag, url? } }, reviews }; reviews are labelled with their style
 *             (tag + colour), or in a bundle (colourways) the colour leads
 *             the byline
 *   fit = { lead, text }: what the reviews say about fit, under the score
 *   review = { id, rating, date, name, verified, variant, body,
 *              photos?: [{ src, full?, focus?, video? }], reply?, pid?, color? }
 *            (video: a Cloudflare Stream id; src is then the video's first frame)
 *   featured = review ids to show first. Optional: without it the strongest
 *              reviews are chosen here (rank/fill).
 *
 * A quiet summary (score, count, rating bars) beside a short list of the
 * best reviews; every review, negative ones included, is one tap away in the
 * sheet, with filters and sorting. A grouped product opens on the whole
 * group ("All Trento"), with its own reviews ("This shoe") a tab away once
 * it has enough of them.
 */

(() => {
  if (customElements.get('ss-reviews')) return;

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const PAGE_SIZE = 10;
  // Below this average nothing is picked as a highlight: hand-picked praise
  // over a low score reads as cherry-picking. The latest reviews show instead.
  const FEATURE_MIN_AVERAGE = 3.5;
  const ICON = {
    check:
      '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d="M1 5.2 3.8 8 9 2" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>',
  };

  const escapeHTML = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const formatDate = (iso) => {
    const [y, m, d] = String(iso).split('-').map(Number);
    return y && m && d ? `${MONTHS[m - 1]} ${d}, ${y}` : '';
  };
  const oneLine = (text) => String(text || '').replace(/\s+/g, ' ').trim();
  const letterCount = (text) => (String(text).match(/\p{L}/gu) || []).length;
  const hasPhotos = (review) => Array.isArray(review.photos) && review.photos.length > 0;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);
  const singular = (noun, count) => (count === 1 ? noun.replace(/s$/, '') : noun);

  // one decimal, rounded the way the stars read: 4.77 -> "4.8"
  const ratingText = (value) => (Math.round(value * 10) / 10).toFixed(1);

  // links to other products keep an alternate template (?view=) when the
  // page itself is on one
  const withView = (url) => {
    const view = new URLSearchParams(window.location.search).get('view');
    return view ? url + (url.includes('?') ? '&' : '?') + 'view=' + encodeURIComponent(view) : url;
  };

  // filled to the exact average (rounding would pull a 4.7 down to 4.5)
  function stars(rating, label, modifier = '') {
    const fill = Math.max(0, Math.min(100, (rating / 5) * 100));
    const text = label || `${rating} out of 5 stars`;
    return `<span class="srv-stars${modifier}" style="--srv-fill: ${fill}%;" role="img" aria-label="${escapeHTML(text)}"></span>`;
  }

  // withColour: in a bundled design the colour the customer bought leads
  // the variant ("Navy · Size L")
  function byline(review, withColour = false) {
    const parts = [`<span class="srv-byline__name">${escapeHTML(review.name || 'Customer')}</span>`];
    if (review.verified) parts.push(`<span class="srv-byline__verified">${ICON.check}Verified buyer</span>`);
    const variant = [withColour ? review.color : '', review.variant].filter(Boolean).join(' · ');
    if (variant) parts.push(`<span class="srv-byline__variant">${escapeHTML(variant)}</span>`);
    return `<p class="srv-byline">${parts.join('')}</p>`;
  }

  function summarize(reviews) {
    const count = reviews.length;
    return {
      average: count ? reviews.reduce((sum, r) => sum + r.rating, 0) / count : 0,
      count,
      verified: reviews.filter((r) => r.verified).length,
      distribution: [5, 4, 3, 2, 1].map((n) => reviews.filter((r) => r.rating === n).length),
    };
  }

  function barsHTML(summary, asButtons = false) {
    const { distribution, count } = summary;
    return [5, 4, 3, 2, 1]
      .map((n, i) => {
        const value = distribution[i] || 0;
        const inner = `<span class="srv__bar-label" aria-hidden="true">${n}<span class="srv-star"></span></span><span class="srv__bar-track" aria-hidden="true"><span class="srv__bar-fill" style="width: ${
          count ? (value / count) * 100 : 0
        }%;"></span></span><span class="srv__bar-count" aria-hidden="true">${value}</span><span class="visually-hidden">${n} star${
          n > 1 ? 's' : ''
        }: ${value} review${value === 1 ? '' : 's'}</span>`;
        if (!asButtons) return `<li class="srv__bar">${inner}</li>`;
        return `<li><button type="button" class="srv__bar srv__bar--button" data-filter="${n}"${value ? '' : ' disabled'}>${inner}</button></li>`;
      })
      .join('');
  }

  // One review: stars + date, text, photos, which product (in a pool), name.
  function itemHTML(review, tag, withColour = false) {
    const photos = hasPhotos(review)
      ? `<div class="srv-item__photos">${review.photos
          .map(
            (photo, index) =>
              `<button type="button" class="srv-item__photo" data-srv-photo data-review="${escapeHTML(review.id)}" data-index="${index}" aria-label="${
                photo.video ? 'Play video' : 'View photo'
              } ${index + 1} of ${review.photos.length} from ${escapeHTML(review.name)}" aria-haspopup="dialog"><img src="${escapeHTML(
                photo.src
              )}" alt="" loading="lazy" width="80" height="80"></button>`
          )
          .join('')}</div>`
      : '';
    const body = String(review.body || '').trim();
    const text = body ? `<p class="srv-item__text">${escapeHTML(body).replace(/\n+/g, '<br>')}</p>` : '';
    const reply = review.reply
      ? `<div class="srv-item__reply"><p class="srv-item__reply-label">Santo Studio</p><p>${escapeHTML(review.reply)}</p></div>`
      : '';
    return `<li class="srv-item"><div class="srv-item__top">${stars(review.rating)}<time class="srv-item__date" datetime="${escapeHTML(
      review.date
    )}">${formatDate(review.date)}</time></div>${text}${photos}${tag || ''}${byline(review, withColour)}${reply}</li>`;
  }

  /* --- Choosing what shows ------------------------------------------------
     Curated `featured` ids win. Otherwise: 4-5 star reviews with something
     to say, favouring photos and substance, demoting ones that are really
     about a problem (those still sit in the full list). One per customer. */

  const PROBLEM = /\b(defect\w*|ripp?\w*|tear\w*|tore|broke\w*|return\w*|refund\w*|exchang\w*|too (small|big|tight|short|long)|wish|disappoint\w*|faded?|unprofessional)\b/i;

  function score(review) {
    const length = oneLine(review.body).length;
    return (
      (review.rating === 5 ? 3 : 1) +
      Math.min(length, 240) / 60 -
      (length > 320 ? (length - 320) / 80 : 0) +
      (hasPhotos(review) ? 2.5 : 0) +
      (review.verified ? 0.5 : 0) -
      (PROBLEM.test(review.body) ? 2.5 : 0)
    );
  }

  // 4-5 star reviews, ones with photos first (a photo needs no minimum of
  // text), then the strongest with at least `minLetters` letters
  const rank = (reviews, boost = () => 0, minLetters = 30) =>
    reviews
      .filter((r) => r.rating >= 4 && (hasPhotos(r) || letterCount(r.body) >= minLetters))
      .sort((a, b) => Number(hasPhotos(b)) - Number(hasPhotos(a)) || score(b) + boost(b) - (score(a) + boost(a)));

  // reviews with photos to the top, order otherwise kept
  const photosFirst = (reviews) => reviews.filter(hasPhotos).concat(reviews.filter((r) => !hasPhotos(r)));

  // Adds candidates in order to `picked` up to `count`: one review per
  // customer, at most `perProduct` from one product of a pool.
  function fill(picked, candidates, count, perProduct = Infinity) {
    const out = picked.slice();
    const names = new Set(out.map((r) => r.name));
    const perCount = {};
    out.forEach((r) => (perCount[r.pid] = (perCount[r.pid] || 0) + 1));
    for (const review of candidates) {
      if (out.length >= count) break;
      if (out.includes(review) || names.has(review.name)) continue;
      if (review.pid && (perCount[review.pid] || 0) >= perProduct) continue;
      names.add(review.name);
      perCount[review.pid] = (perCount[review.pid] || 0) + 1;
      out.push(review);
    }
    return out;
  }

  // Stable per product, so a page shows the same reviews on every visit but
  // different pages don't all show the same ones.
  const seedOf = (text) => {
    let hash = 5381;
    for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) >>> 0;
    return hash;
  };
  function seededShuffle(list, seed) {
    let state = seed >>> 0;
    const random = () => {
      // mulberry32
      state = (state + 0x6d2b79f5) >>> 0;
      let t = Math.imul(state ^ (state >>> 15), 1 | state);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const out = list.slice();
    for (let i = out.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }
  const SHUFFLE_WINDOW = 15;

  function selectReviews(scope, byId, count, productId) {
    const { reviews, summary } = scope;
    const inScope = new Set(reviews.map((r) => r.id));
    const curated = (scope.featured || []).map((id) => byId.get(String(id))).filter((r) => r && inScope.has(r.id));
    let picked = [];
    if (scope.pooled) {
      // The product's own best two lead (when its own average holds up);
      // the rest come from its other styles, at most two per style,
      // shuffled per page so sibling pages don't all show the same ones.
      if (summary.average >= FEATURE_MIN_AVERAGE) {
        const own = reviews.filter((r) => String(r.pid) === productId);
        const ownAverage = own.length ? own.reduce((sum, r) => sum + r.rating, 0) / own.length : 0;
        const lead = curated.length ? curated.slice(0, 2) : ownAverage >= FEATURE_MIN_AVERAGE ? fill([], rank(own), 2) : [];
        const ranked = rank(reviews.filter((r) => String(r.pid) !== productId));
        // shuffled per page within each group, photo reviews ahead
        const seed = seedOf(productId);
        const shuffleTop = (list) => seededShuffle(list.slice(0, SHUFFLE_WINDOW), seed).concat(list.slice(SHUFFLE_WINDOW));
        const others = shuffleTop(ranked.filter(hasPhotos)).concat(shuffleTop(ranked.filter((r) => !hasPhotos(r))));
        // at most two per style, unless that leaves slots empty (a pool of
        // two listings); the product's own only when its average holds up
        const rest = ownAverage >= FEATURE_MIN_AVERAGE ? rank(reviews) : ranked;
        picked = fill(fill(lead, others, count, 2), rest, count);
      }
    } else if (curated.length) {
      picked = curated.slice(0, count);
    } else if (summary.average >= FEATURE_MIN_AVERAGE) {
      // substantive reviews first; shorter ones fill any slots left
      picked = fill(fill([], rank(reviews), count), rank(reviews, undefined, 12), count);
    }
    // nothing to pick (a low average, or only short reviews): the latest, as
    // they are, whatever their rating
    if (!picked.length) picked = reviews.filter((r) => oneLine(r.body) || hasPhotos(r)).slice(0, count);
    return photosFirst(picked);
  }

  // what the reviews say about fit ("Runs small. ..."), under the score
  const fitHTML = (fit) =>
    fit && fit.text ? `<p class="srv-fit"><strong class="srv-fit__lead">${escapeHTML(fit.lead || '')}</strong> ${escapeHTML(fit.text)}</p>` : '';

  /* --- Scroll lock shared by the sheet and the photo viewer --- */
  let locks = 0;
  const lockScroll = () => {
    if (locks++ === 0) document.documentElement.classList.add('srv-lock');
  };
  const unlockScroll = () => {
    locks = Math.max(0, locks - 1);
    if (locks === 0) document.documentElement.classList.remove('srv-lock');
  };

  // one request per data file, however many sections ask
  const dataFiles = {};
  const fetchData = (url) =>
    (dataFiles[url] =
      dataFiles[url] ||
      fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error('reviews data ' + response.status);
        return response.json();
      }));

  class SSReviews extends HTMLElement {
    connectedCallback() {
      if (this.__srvInit) return;
      this.__srvInit = true;

      const inline = this.querySelector('script[type="application/json"]');
      if (inline) {
        let entry = null;
        try {
          entry = JSON.parse(inline.textContent);
        } catch (error) {
          entry = null;
        }
        this.init(entry, null);
        return;
      }
      if (!this.dataset.src) {
        this.init(null, null);
        return;
      }
      const load = () =>
        fetchData(this.dataset.src)
          .then((all) => this.init(all && all.products ? all.products[this.dataset.productId] : null, all))
          .catch(() => this.init(null, null));
      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }
      // fetched well before it scrolls into view
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '1200px 0px' }
      );
      observer.observe(this);
    }

    init(entry, all) {
      this.productId = String(this.dataset.productId || '');
      // fewest reviews a product needs before its own show (a count, never a
      // rating); the section's Liquid applies it to unpooled products
      this.min = Math.max(1, Number(this.dataset.min || 1));
      const pool = entry && entry.pool && all && all.pools ? all.pools[entry.pool] : null;
      const scopes = [];
      if (pool && Array.isArray(pool.reviews) && pool.reviews.length) {
        this.poolProducts = pool.products || {};
        scopes.push({ key: 'pool', label: pool.label || 'All', noun: pool.noun || 'reviews', reviews: pool.reviews, featured: entry.featured, pooled: true, bundle: Boolean(pool.bundle), fit: pool.fit });
        const own = pool.reviews.filter((r) => String(r.pid) === this.productId);
        if (own.length >= this.min) scopes.push({ key: 'own', label: pool.ownLabel || 'This product', noun: 'reviews', reviews: own, featured: entry.featured, fit: pool.fit });
      } else if (entry && Array.isArray(entry.reviews) && entry.reviews.length) {
        scopes.push({ key: 'own', noun: 'reviews', reviews: entry.reviews, summary: entry.summary, featured: entry.featured, fit: entry.fit });
      }
      if (!scopes.length) {
        // nothing to show: drop the padded wrapper too
        const wrapper = this.closest('.srv-section');
        if (wrapper) wrapper.hidden = true;
        this.hidden = true;
        return;
      }
      scopes.forEach((scope) => {
        scope.summary = scope.summary || summarize(scope.reviews);
      });

      this.scopes = scopes;
      this.byId = new Map();
      scopes.forEach((scope) => scope.reviews.forEach((r) => this.byId.set(String(r.id), r)));
      this.uid = 'srv-' + Math.random().toString(36).slice(2, 8);
      this.count = Math.max(1, Number(this.dataset.count || 4));
      this.sheets = {};
      this.addEventListener('click', this.onClick.bind(this));
      this.show(scopes[0].key);
    }

    show(key) {
      const scope = this.scopes.find((s) => s.key === key) || this.scopes[0];
      this.scope = scope;
      if (!scope.picked) scope.picked = selectReviews(scope, this.byId, this.count, this.productId);
      this.render();
    }

    // the style a grouped review was for ("Cole Houshmand Edition · White"),
    // linked when it's another listing; colourway bundles put the colour in
    // the byline instead
    productTag(scope, review) {
      if (!scope.pooled || scope.bundle || !review.pid) return '';
      const product = this.poolProducts[review.pid] || {};
      const text = [product.tag, review.color].filter(Boolean).join(' · ');
      if (!text) return '';
      const link = product.url && String(review.pid) !== this.productId;
      return `<p class="srv-product">${link ? `<a href="${escapeHTML(withView(product.url))}">${escapeHTML(text)}</a>` : escapeHTML(text)}</p>`;
    }

    render() {
      const scope = this.scope;
      const { summary, picked } = scope;
      const average = ratingText(summary.average);
      const headingId = this.uid + '-heading';
      // no way into the sheet when every review is already on the page
      const cta = (modifier) =>
        picked.length >= summary.count
          ? ''
          : `<button type="button" class="srv__cta ${modifier}" data-srv-open aria-haspopup="dialog">Read all ${summary.count} ${escapeHTML(scope.noun)}</button>`;
      const tabs =
        this.scopes.length > 1
          ? `<div class="srv__tabs" role="group" aria-label="Show reviews for">${this.scopes
              .map(
                (s) =>
                  `<button type="button" class="srv__tab" data-scope="${s.key}" aria-pressed="${s === scope}">${escapeHTML(s.label)}<span class="srv__tab-count">${
                    s.summary.count
                  }</span></button>`
              )
              .join('')}</div>`
          : '';

      this.setAttribute('role', 'region');
      this.setAttribute('aria-labelledby', headingId);
      this.innerHTML = `
        <div class="srv__inner">
          <div class="srv__summary">
            <h2 class="srv__heading" id="${headingId}">${escapeHTML(this.dataset.heading || 'Reviews')}</h2>
            <div class="srv__score">
              <span class="srv__score-value" aria-hidden="true">${average}</span>
              <span class="srv__score-meta">
                ${stars(summary.average, `Rated ${average} out of 5`)}
                <span class="srv__score-count">Based on ${summary.count} ${escapeHTML(singular(scope.noun, summary.count))}</span>
              </span>
            </div>
            ${fitHTML(scope.fit)}
            ${tabs}
            <ul class="srv__bars" role="list">${barsHTML(summary)}</ul>
            ${cta('srv__cta--side')}
          </div>
          <ul class="srv-list srv__list" role="list">${picked.map((review) => itemHTML(review, this.productTag(scope, review), scope.bundle)).join('')}</ul>
        </div>
        <div class="srv__foot">${cta('srv__cta--foot')}</div>`;
    }

    onClick(event) {
      const tab = event.target.closest('[data-scope]');
      if (tab) {
        const key = tab.dataset.scope;
        if (key !== this.scope.key) {
          this.show(key);
          const again = this.querySelector(`[data-scope="${key}"]`);
          if (again) again.focus({ preventScroll: true });
        }
        return;
      }
      const photo = event.target.closest('[data-srv-photo]');
      if (photo) {
        openViewer(this, photo);
        return;
      }
      const opener = event.target.closest('[data-srv-open]');
      if (opener) {
        const key = this.scope.key;
        if (!this.sheets[key]) this.sheets[key] = new ReviewSheet(this, this.scope);
        this.sheets[key].open(opener);
      }
    }
  }

  /* --- Full list: every review in a scope, with filters and sorting --- */
  class ReviewSheet {
    constructor(host, scope) {
      this.host = host;
      this.scope = scope;
      this.filter = 'all';
      this.shown = PAGE_SIZE;

      const { summary, reviews } = scope;
      const average = ratingText(summary.average);
      const titleId = host.uid + '-' + scope.key + '-sheet-title';
      const photoCount = reviews.filter(hasPhotos).length;
      // opens on the highest rated, as the sort menu says; the rating bars
      // and star filters above keep every low review one tap away
      this.sort = 'highest';
      const chips = [{ key: 'all', label: 'All', count: summary.count }];
      if (photoCount) chips.push({ key: 'photos', label: 'With photos', count: photoCount });
      [5, 4, 3, 2, 1].forEach((n, i) => {
        if (summary.distribution[i]) {
          chips.push({ key: String(n), label: `${n}<span class="srv-star" aria-hidden="true"></span>`, name: `${n} star`, count: summary.distribution[i] });
        }
      });

      const dialog = document.createElement('dialog');
      dialog.className = 'srv-sheet';
      dialog.setAttribute('aria-labelledby', titleId);
      dialog.innerHTML = `
        <div class="srv-sheet__panel" tabindex="-1">
          <div class="srv-sheet__head">
            <h2 class="srv-sheet__title" id="${titleId}">${escapeHTML(capitalize(scope.noun))} <span class="srv-sheet__title-count">(${summary.count})</span></h2>
            <button type="button" class="srv-sheet__close">Close</button>
          </div>
          <div class="srv-sheet__scroll">
            <div class="srv-sheet__summary">
              <div class="srv-sheet__score">
                <span class="srv-sheet__average" aria-hidden="true">${average}</span>
                <div class="srv-sheet__score-meta">
                  ${stars(summary.average, `Rated ${average} out of 5`, ' srv-stars--lg')}
                  <p class="srv-sheet__based">Based on ${summary.count} ${escapeHTML(singular(scope.noun, summary.count))}</p>
                </div>
              </div>
              <ul class="srv__bars srv-sheet__bars" role="list" aria-label="Filter by rating">${barsHTML(summary, true)}</ul>
              ${fitHTML(scope.fit)}
            </div>
            <div class="srv-sheet__controls">
              <div class="srv-sheet__chips" role="group" aria-label="Filter reviews">
                ${chips
                  .map(
                    (chip) =>
                      `<button type="button" class="srv-chip" data-filter="${chip.key}" aria-pressed="${chip.key === 'all'}"${
                        chip.name ? ` aria-label="${chip.name}, ${chip.count} reviews"` : ''
                      }>${chip.label}<span class="srv-chip__count">${chip.count}</span></button>`
                  )
                  .join('')}
              </div>
              <label class="srv-sheet__sort">
                <span class="srv-sheet__sort-label">Sort</span>
                <select class="srv-sheet__select">
                  <option value="highest" selected>Highest rated</option>
                  <option value="detail">Most detailed</option>
                  <option value="newest">Newest</option>
                  <option value="lowest">Lowest rated</option>
                  ${photoCount ? '<option value="photos">With photos first</option>' : ''}
                </select>
              </label>
            </div>
            <p class="visually-hidden" role="status" aria-live="polite" data-srv-status></p>
            <ul class="srv-list" role="list"></ul>
            <p class="srv-sheet__empty" hidden>No reviews match this filter.</p>
            <button type="button" class="srv__cta srv-sheet__more" hidden>Show more reviews</button>
          </div>
        </div>`;
      document.body.appendChild(dialog);

      this.dialog = dialog;
      this.panel = dialog.querySelector('.srv-sheet__panel');
      this.scroller = dialog.querySelector('.srv-sheet__scroll');
      this.list = dialog.querySelector('.srv-list');
      this.empty = dialog.querySelector('.srv-sheet__empty');
      this.more = dialog.querySelector('.srv-sheet__more');
      this.status = dialog.querySelector('[data-srv-status]');
      this.select = dialog.querySelector('.srv-sheet__select');

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) {
          dialog.close();
          return;
        }
        const photo = event.target.closest('[data-srv-photo]');
        if (photo) {
          openViewer(this.host, photo, this.scope);
          return;
        }
        const filter = event.target.closest('[data-filter]');
        if (filter && !filter.disabled) {
          this.setFilter(filter.dataset.filter);
          return;
        }
        if (event.target.closest('.srv-sheet__close')) dialog.close();
      });
      this.select.addEventListener('change', () => {
        this.sort = this.select.value;
        this.shown = PAGE_SIZE;
        this.renderList(true);
      });
      this.more.addEventListener('click', () => {
        this.shown += PAGE_SIZE;
        this.renderList(false);
      });
      dialog.addEventListener('close', () => {
        unlockScroll();
        if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      });
    }

    open(opener) {
      this.opener = opener;
      this.renderList(true);
      lockScroll();
      this.dialog.showModal();
      this.panel.focus({ preventScroll: true });
    }

    setFilter(key) {
      this.filter = key;
      this.shown = PAGE_SIZE;
      this.dialog.querySelectorAll('.srv-chip').forEach((chip) => chip.setAttribute('aria-pressed', String(chip.dataset.filter === key)));
      this.renderList(true);
      const controls = this.dialog.querySelector('.srv-sheet__controls');
      if (controls.getBoundingClientRect().top < this.scroller.getBoundingClientRect().top) {
        this.scroller.scrollTo({ top: controls.offsetTop - 12, behavior: reducedMotion() ? 'auto' : 'smooth' });
      }
    }

    filtered() {
      const { filter, sort } = this;
      let items = this.scope.reviews.slice();
      if (filter === 'photos') items = items.filter(hasPhotos);
      else if (filter !== 'all') items = items.filter((r) => r.rating === Number(filter));
      const byDate = (a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0);
      // most detailed: photos, then the most words (capped, so an essay
      // doesn't beat every photo), then newest; stars play no part
      const detail = (r) => Math.min((String(r.body).match(/\p{L}+/gu) || []).length, 60);
      if (sort === 'detail') items.sort((a, b) => Number(hasPhotos(b)) - Number(hasPhotos(a)) || detail(b) - detail(a) || byDate(a, b));
      // highest rated: within the same stars, photos and the most detailed first
      else if (sort === 'highest') items.sort((a, b) => b.rating - a.rating || Number(hasPhotos(b)) - Number(hasPhotos(a)) || detail(b) - detail(a) || byDate(a, b));
      else if (sort === 'lowest') items.sort((a, b) => a.rating - b.rating || byDate(a, b));
      else if (sort === 'photos') items.sort((a, b) => Number(hasPhotos(b)) - Number(hasPhotos(a)) || byDate(a, b));
      else items.sort(byDate);
      return items;
    }

    renderList(reset) {
      const items = this.filtered();
      const visible = items.slice(0, this.shown);
      const start = reset ? 0 : this.list.children.length;
      const html = visible
        .slice(start)
        .map((review) => itemHTML(review, this.host.productTag(this.scope, review), this.scope.bundle))
        .join('');
      if (reset) this.list.innerHTML = html;
      else this.list.insertAdjacentHTML('beforeend', html);
      this.empty.hidden = items.length > 0;
      this.more.hidden = visible.length >= items.length;
      if (reset) this.status.textContent = `Showing ${items.length} review${items.length === 1 ? '' : 's'}`;
    }
  }

  /* --- Photo viewer, shared by the section and the sheet --- */
  let viewer = null;

  function openViewer(host, button, scope = host.scope) {
    const review = host.byId.get(String(button.dataset.review));
    if (!review || !hasPhotos(review)) return;
    if (!viewer) viewer = new PhotoViewer();
    viewer.open(review, Number(button.dataset.index || 0), button, host.productTag(scope, review), scope.bundle);
  }

  class PhotoViewer {
    constructor() {
      const dialog = document.createElement('dialog');
      dialog.className = 'srv-lb';
      dialog.setAttribute('aria-label', 'Review photos');
      // the panel takes focus on open, so CLOSE doesn't light up
      dialog.tabIndex = -1;
      dialog.innerHTML = `
        <div class="srv-lb__media"><img class="srv-lb__img" alt=""></div>
        <div class="srv-lb__side">
          <div class="srv-lb__head"><button type="button" class="srv-lb__close">Close</button></div>
          <div class="srv-lb__thumbs" role="group" aria-label="Photos"></div>
          <p class="visually-hidden" aria-live="polite" data-srv-photo-status></p>
          <div class="srv-lb__review"></div>
        </div>`;
      document.body.appendChild(dialog);
      this.dialog = dialog;
      this.img = dialog.querySelector('.srv-lb__img');
      this.media = dialog.querySelector('.srv-lb__media');
      this.thumbs = dialog.querySelector('.srv-lb__thumbs');
      this.status = dialog.querySelector('[data-srv-photo-status]');
      this.review = dialog.querySelector('.srv-lb__review');

      dialog.querySelector('.srv-lb__close').addEventListener('click', () => dialog.close());
      this.thumbs.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-index]');
        if (thumb) this.show(Number(thumb.dataset.index));
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('keydown', (event) => {
        if (this.photos.length < 2) return;
        if (event.key === 'ArrowLeft') this.show(this.index - 1);
        if (event.key === 'ArrowRight') this.show(this.index + 1);
      });
      let touchX = null;
      this.media.addEventListener('touchstart', (event) => (touchX = event.touches[0].clientX), { passive: true });
      this.media.addEventListener('touchend', (event) => {
        if (touchX === null || this.photos.length < 2) return;
        const dx = event.changedTouches[0].clientX - touchX;
        touchX = null;
        if (Math.abs(dx) > 40) this.show(this.index + (dx < 0 ? 1 : -1));
      });
      dialog.addEventListener('close', () => {
        unlockScroll();
        this.img.removeAttribute('src');
        this.stopVideo();
        if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      });
    }

    open(review, index, opener, tag, withColour = false) {
      this.photos = review.photos;
      this.opener = opener;
      const multi = this.photos.length > 1;
      this.thumbs.hidden = !multi;
      this.thumbs.innerHTML = multi
        ? this.photos
            .map(
              (photo, i) =>
                `<button type="button" class="srv-lb__thumb" data-index="${i}" aria-label="${photo.video ? 'Video' : 'Photo'} ${i + 1} of ${this.photos.length}"><img src="${escapeHTML(
                  photo.src
                )}" alt="" width="64" height="64"></button>`
            )
            .join('')
        : '';
      const body = String(review.body || '').trim();
      this.review.innerHTML = `<div class="srv-item__top">${stars(review.rating)}<time class="srv-item__date" datetime="${escapeHTML(
        review.date
      )}">${formatDate(review.date)}</time></div>${
        body ? `<p class="srv-item__text">${escapeHTML(body).replace(/\n+/g, '<br>')}</p>` : ''
      }${tag || ''}${byline(review, withColour)}`;
      this.img.alt = `Photo from ${review.name}`;
      this.name = review.name;
      this.show(index);
      lockScroll();
      this.dialog.showModal();
      this.dialog.focus({ preventScroll: true });
    }

    // a video review plays in Cloudflare Stream's player (where Loox keeps
    // it), in place of the image; removing the player stops it
    stopVideo() {
      const player = this.media.querySelector('.srv-lb__video');
      if (player) player.remove();
    }

    show(index) {
      this.index = (index + this.photos.length) % this.photos.length;
      const photo = this.photos[this.index];
      const img = this.img;
      this.stopVideo();
      const label = `${photo.video ? 'Video' : 'Photo'} ${this.index + 1} of ${this.photos.length}`;
      this.thumbs.querySelectorAll('.srv-lb__thumb').forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === this.index)));
      this.status.textContent = this.photos.length > 1 ? label : '';
      if (/^[0-9a-f]{32}$/.test(photo.video || '')) {
        img.hidden = true;
        img.removeAttribute('src');
        const player = document.createElement('iframe');
        player.className = 'srv-lb__video';
        player.src = `https://iframe.videodelivery.net/${photo.video}?autoplay=true&muted=true&preload=auto`;
        player.title = `Video from ${this.name}`;
        player.allow = 'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture';
        player.allowFullscreen = true;
        this.media.appendChild(player);
        return;
      }
      img.hidden = false;
      img.classList.add('is-loading');
      img.onload = () => img.classList.remove('is-loading');
      img.onerror = () => {
        img.onerror = null;
        if (photo.src && img.src !== photo.src) img.src = photo.src;
        else img.classList.remove('is-loading');
      };
      img.src = photo.full || photo.src;
      // a curated focal point for the 4:5 crop, e.g. "50% 30%"
      img.style.objectPosition = /^[\d.]+% [\d.]+%$/.test(photo.focus || '') ? photo.focus : '';
    }
  }

  /* --- The stars under the title (snippets/ss-reviews-stars.liquid) --------
     Eased scroll down to the reviews. It re-aims every frame, so sections
     above that grow as their images load can't make it overshoot; a wheel or
     touch by the visitor takes over. Lands 24px under whatever is pinned and
     showing at the top: the announcement bar on phones, plus the header
     while it's visible (on phones it fades out while scrolling down). */
  const showing = (el) => {
    for (let node = el; node && node !== document.documentElement; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (style.visibility === 'hidden' || parseFloat(style.opacity) < 0.1) return false;
    }
    return true;
  };
  const pinnedBottom = () => {
    let bottom = 0;
    document.querySelectorAll('.announcement-bar-section, sticky-header').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.height && rect.bottom > 0 && rect.top <= bottom + 1 && showing(el)) bottom = Math.max(bottom, rect.bottom);
    });
    return bottom;
  };
  function gentleScrollTo(target) {
    const aim = () => target.getBoundingClientRect().top + window.scrollY - (pinnedBottom() + 24);
    if (reducedMotion()) {
      window.scrollTo({ top: aim(), behavior: 'instant' });
      return;
    }
    const startY = window.scrollY;
    const duration = Math.min(1400, Math.max(700, Math.abs(aim() - startY) * 0.35));
    let startTime = null;
    let cancelled = false;
    const cancel = () => (cancelled = true);
    window.addEventListener('wheel', cancel, { once: true, passive: true });
    window.addEventListener('touchstart', cancel, { once: true, passive: true });
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const step = (now) => {
      if (cancelled) return;
      if (startTime === null) startTime = now;
      const t = Math.min(1, (now - startTime) / duration);
      window.scrollTo({ top: startY + (aim() - startY) * ease(t), behavior: 'instant' });
      if (t < 1) requestAnimationFrame(step);
      else {
        window.removeEventListener('wheel', cancel);
        window.removeEventListener('touchstart', cancel);
      }
    };
    requestAnimationFrame(step);
  }
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a.pdp-stars');
    if (!link) return;
    const target = document.querySelector(link.getAttribute('href'));
    if (!target) return;
    event.preventDefault();
    gentleScrollTo(target);
  });

  customElements.define('ss-reviews', SSReviews);
})();
